import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function WhatToEatTonightPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="What to Eat Tonight? — Decide in 2 Minutes with Hangrily"
        description="What should I eat tonight? Hangrily ends dinner indecision in under 2 minutes. Set your mood, cuisine & budget, then swipe through nearby restaurants with real photos & ratings. Works solo, with your partner, or with up to 10 friends. Free."
        path="/what-to-eat-tonight"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'What to Eat Tonight - Hangrily',
            description: 'End dinner indecision. Swipe through restaurants and decide what to eat tonight in under 2 minutes.',
            url: 'https://hangrily.app/what-to-eat-tonight'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'What to Eat Tonight', item: 'https://hangrily.app/what-to-eat-tonight' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to Decide What to Eat Tonight',
            description: 'Use Hangrily to decide what to eat tonight in 3 simple steps.',
            totalTime: 'PT2M',
            step: [
              { '@type': 'HowToStep', position: 1, name: 'Set your mood and preferences', text: 'Open Hangrily and select your mood (comfort food, adventurous, healthy, etc.), preferred cuisines, and budget. The app curates a list of nearby restaurants that match.' },
              { '@type': 'HowToStep', position: 2, name: 'Swipe through options', text: 'See restaurant cards with real photos, ratings, and distances. Swipe right on places that look good, left on ones that do not appeal to you.' },
              { '@type': 'HowToStep', position: 3, name: 'Get your answer', text: 'The app picks the restaurant with the most likes. In couple or group mode, it finds the restaurant everyone agreed on. Decision made in under 2 minutes.' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'I literally have no idea what I want to eat tonight. Can Hangrily help?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely! That\'s the most common reason people use Hangrily. Just set broad preferences (any cuisine, any price) and start swiping. Seeing real restaurant photos triggers your appetite in ways a text list never will. Most people figure out what they\'re craving within the first few swipes.'
                }
              },
              {
                '@type': 'Question',
                name: 'How is this different from just Googling "restaurants near me"?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Google gives you a list of 200+ options. Hangrily shows you one restaurant at a time with full photos and ratings, and all you have to do is swipe. It\'s the difference between browsing a catalog and playing a game — way less overwhelming and much faster to decide.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I use it to decide on delivery too?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Hangrily finds restaurants near you regardless of how you plan to eat. Once you pick a restaurant, you can order delivery through DoorDash or UberEats right from the results screen, or get directions if you\'re going in person.'
                }
              },
              {
                '@type': 'Question',
                name: 'My partner and I can never agree on dinner. Will this actually help?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'This is literally why Hangrily was built. Couple Mode eliminates the back-and-forth by having both of you swipe independently. You only match on restaurants you both liked. It\'s like Tinder, but instead of finding a date, you\'re finding dinner.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>What Should I Eat Tonight?</h1>
          <p className="hero-subtitle">
            The nightly question that stumps millions. Let's fix that in under 2 minutes.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Why Is "What to Eat" So Hard?</h2>
              <p>
                You're hungry. You know you need to eat. But somehow, deciding <em>where</em> to eat feels 
                like the hardest decision of the day. Psychologists call it <strong>decision fatigue</strong> — 
                after a long day of making choices at work, your brain struggles with even simple decisions.
              </p>
              <p>
                The paradox of choice makes it worse: with hundreds of restaurants, delivery apps, and cuisines 
                available, having <em>too many</em> options actually makes it harder to choose. You end up 
                scrolling endlessly, ordering the same thing as always, or just not eating until you're starving.
              </p>
              <p>
                <strong>Hangrily cuts through the noise.</strong> Tell us your mood, set a few preferences, 
                and we'll show you a curated set of nearby restaurants — one at a time. No endless lists. 
                No analysis paralysis. Just swipe and eat.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Decide What to Eat →
              </button>
            </div>
            <div className="column-features">
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">🧠</span>
                <h3>Mood-Based</h3>
                <p>Feeling adventurous? Craving comfort food? Set your mood and get matching restaurants.</p>
              </div>
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">⚡</span>
                <h3>Quick Decision</h3>
                <p>3 rounds of swiping, 15 restaurants total. Done in under 2 minutes.</p>
              </div>
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">🔄</span>
                <h3>Never Repeat</h3>
                <p>Track your dining history so you don't end up at the same place every night.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>What to Eat Tonight — By Mood</h2>
          <p style={{ maxWidth: '700px', margin: '0 auto 32px', opacity: 0.7, textAlign: 'center' }}>
            Not sure what cuisine you want? Start with your mood and let Hangrily do the rest:
          </p>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-emoji">🛋️</span>
              <h3>Comfort Food Night</h3>
              <p>Mac & cheese, burgers, pizza, fried chicken — the warm-hug meals that make everything better.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">🌍</span>
              <h3>Adventurous Eating</h3>
              <p>Try something new — Ethiopian, Korean BBQ, Peruvian. Hangrily shows you cuisines you haven't tried.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">🥗</span>
              <h3>Healthy Tonight</h3>
              <p>Salads, poke bowls, Mediterranean. Filter for lighter options and healthy restaurants near you.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">💰</span>
              <h3>Budget-Friendly</h3>
              <p>Set your price filter to $ or $$ and find delicious meals that won't break the bank tonight.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">🥂</span>
              <h3>Treat Yourself</h3>
              <p>Upscale dining, tasting menus, cocktail bars. Filter to $$$ and $$$$ for a special evening.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">⏰</span>
              <h3>Quick Bite</h3>
              <p>No time to sit down? Filter for fast food or fast-casual spots for a quick, satisfying meal.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>How Hangrily Decides for You</h2>
          <div className="steps-grid">
            <div className="step-card">
              <span className="step-number">1</span>
              <h3>Tell Us Your Mood</h3>
              <p>Open Hangrily and pick what sounds good tonight. Comfort? Adventurous? Healthy? Quick? The app curates your restaurant options accordingly.</p>
            </div>
            <div className="step-card">
              <span className="step-number">2</span>
              <h3>Swipe Through Options</h3>
              <p>See 10-15 restaurant cards with photos, ratings, distance, and price. Swipe right on the ones that catch your eye, left on the rest.</p>
            </div>
            <div className="step-card">
              <span className="step-number">3</span>
              <h3>Eat</h3>
              <p>The app picks your top match. Tap for directions, order delivery, or just head out. Tonight's dinner — decided.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
              What Should I Eat? →
            </button>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>Works for Everyone at the Table</h2>
          <div className="steps-grid">
            <div className="step-card">
              <h3>🍽️ Eating Alone?</h3>
              <p>Solo Mode narrows your options in 3 quick rounds. No more staring at Uber Eats for 20 minutes.</p>
            </div>
            <div className="step-card">
              <h3>💑 With Your Partner?</h3>
              <p>Couple Mode lets you both swipe independently. Match on restaurants you both liked — no arguments needed.</p>
            </div>
            <div className="step-card">
              <h3>👥 Group Dinner?</h3>
              <p>Group Mode supports up to 10 people. Everyone swipes, and the group's consensus pick wins.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            <details className="faq-item">
              <summary>I literally have no idea what I want to eat tonight. Can Hangrily help?</summary>
              <p>Absolutely! That's the most common reason people use Hangrily. Just set broad preferences (any cuisine, any price) and start swiping. Seeing real restaurant photos triggers your appetite in ways a text list never will. Most people figure out what they're craving within the first few swipes.</p>
            </details>
            <details className="faq-item">
              <summary>How is this different from just Googling "restaurants near me"?</summary>
              <p>Google gives you a list of 200+ options. Hangrily shows you one restaurant at a time with full photos and ratings, and all you have to do is swipe. It's the difference between browsing a catalog and playing a game — way less overwhelming and much faster to decide.</p>
            </details>
            <details className="faq-item">
              <summary>Can I use it to decide on delivery too?</summary>
              <p>Yes! Hangrily finds restaurants near you regardless of how you plan to eat. Once you pick a restaurant, you can order delivery through DoorDash or UberEats right from the results screen, or get directions if you're going in person.</p>
            </details>
            <details className="faq-item">
              <summary>My partner and I can never agree on dinner. Will this actually help?</summary>
              <p>This is literally why Hangrily was built. Couple Mode eliminates the back-and-forth by having both of you swipe independently. You only match on restaurants you both liked. It's like Tinder, but instead of finding a date, you're finding dinner.</p>
            </details>
          </div>
        </section>

        <section className="content-section">
          <div className="internal-links-section">
            <h2>More Resources</h2>
            <div className="internal-links-grid">
              <Link to="/where-should-i-eat" className="internal-link">Where Should I Eat?</Link>
              <Link to="/random-restaurant-picker" className="internal-link">Random Restaurant Picker</Link>
              <Link to="/date-night-restaurants" className="internal-link">Date Night Restaurants</Link>
              <Link to="/best-restaurants-near-me" className="internal-link">Best Restaurants Near Me</Link>
              <Link to="/food-decision-fatigue" className="internal-link">Food Decision Fatigue</Link>
              <Link to="/couples-restaurant-picker" className="internal-link">Couples Restaurant Picker</Link>
              <Link to="/group-restaurant-decision" className="internal-link">Group Restaurant Decision</Link>
              <Link to="/locations" className="internal-link">Browse Cities</Link>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Tonight's Dinner, Decided</h2>
          <p>Stop wondering what to eat. Start swiping — you'll be eating in no time.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Get Started Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
